import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalSpaceBetween,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalSpaceBetween,
  BringToFront,
  ChevronDown,
  ChevronDown as Backward,
  ChevronUp as Forward,
  ClipboardPaste,
  Keyboard,
  Paintbrush,
  SendToBack,
  Grid3x3,
  MoveHorizontal,
  MoveVertical,
  MoveDiagonal2,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useToast } from "../ds";
import { DENSITY_COLUMNS, align, centerOnPage, compactUp, convertDensity, densityOf, distribute, matchSize, straighten, tidyRows, type AlignMode, type Density } from "./layoutTools";
import { isBarWidget, type SPage } from "./model";
import type { StackOp } from "./zorder";

type Apply = (fn: (p: SPage) => SPage, key?: string) => void;

function Btn({ icon: Icon, label, onClick, disabled }: { icon: LucideIcon; label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" className="st-arr__btn" title={label} aria-label={label} disabled={disabled} onClick={onClick}>
      <Icon size={16} />
    </button>
  );
}

/** Power BI-style Format > Align / Distribute, plus auto-arrange and grid density. */
export function ArrangeBar({
  page,
  selectedIds,
  apply,
  onSelectAll,
  onRestack,
  onCopyFormat,
  onPasteFormat,
  canPasteFormat,
  onShortcuts,
}: {
  page: SPage;
  selectedIds: string[];
  apply: Apply;
  onSelectAll: () => void;
  /** Bring to front / Send to back / Forward / Backward for the selection. */
  onRestack: (op: StackOp) => void;
  onCopyFormat: () => void;
  onPasteFormat: () => void;
  canPasteFormat: boolean;
  onShortcuts: () => void;
}) {
  const toast = useToast();
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const onGrid = selectedIds.filter((id) => page.widgets.some((w) => w.id === id && !isBarWidget(w)));
  const n = onGrid.length;
  const density = densityOf(page);

  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenu(false);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [menu]);

  const run = (fn: (p: SPage) => SPage, message: string) => {
    apply(fn);
    setMenu(false);
    toast.info(message, "Not what you wanted? Press Ctrl+Z to undo.");
  };
  const al = (m: AlignMode) => apply((p) => align(p, onGrid, m));
  const toPage = n === 1 ? " (to the page)" : "";

  const setDensity = (to: Density) => {
    if (density === to) return;
    const result = convertDensity(page, to);
    apply(() => result.page);
    toast.success(
      to === "fine" ? "Fine grid on" : "Standard grid on",
      to === "fine" ? "Twice as many columns and rows — everything stays exactly where it was." : result.lossy ? "Some visuals were rounded to the nearest standard cell." : "Everything stays exactly where it was.",
    );
  };

  return (
    <div className="st-arr" role="toolbar" aria-label="Arrange visuals">
      <div className="st-arr__sel">
        <span className="st-arr__hint">
          {n === 0 ? "Select visuals to line them up: drag a box around them, or hold Shift and click each one." : n === 1 ? "1 selected. Hold Shift and click more, or drag a box, to select several." : `${n} selected. Use the buttons to line them up.`}
        </span>
        <button type="button" className="st-arr__seg" onClick={onSelectAll}>Select all</button>
      </div>
      <div className="st-arr__group">
        <Btn icon={AlignStartVertical} label={`Align left${toPage}`} onClick={() => al("left")} disabled={n === 0} />
        <Btn icon={AlignCenterVertical} label={`Align center${toPage}`} onClick={() => al("center")} disabled={n === 0} />
        <Btn icon={AlignEndVertical} label={`Align right${toPage}`} onClick={() => al("right")} disabled={n === 0} />
        <Btn icon={AlignStartHorizontal} label={`Align top${toPage}`} onClick={() => al("top")} disabled={n === 0} />
        <Btn icon={AlignCenterHorizontal} label={`Align middle${toPage}`} onClick={() => al("middle")} disabled={n === 0} />
        <Btn icon={AlignEndHorizontal} label={`Align bottom${toPage}`} onClick={() => al("bottom")} disabled={n === 0} />
      </div>
      <div className="st-arr__group">
        <Btn icon={AlignHorizontalSpaceBetween} label="Distribute horizontally (3 or more)" onClick={() => apply((p) => distribute(p, onGrid, "h"))} disabled={n < 3} />
        <Btn icon={AlignVerticalSpaceBetween} label="Distribute vertically (3 or more)" onClick={() => apply((p) => distribute(p, onGrid, "v"))} disabled={n < 3} />
      </div>
      <div className="st-arr__group">
        <Btn icon={MoveHorizontal} label={n ? "Center selection on the page, horizontally" : "Center everything on the page, horizontally"} onClick={() => apply((p) => centerOnPage(p, onGrid, "h"))} />
        <Btn icon={MoveVertical} label={n ? "Center selection on the page, vertically" : "Center everything on the page, vertically"} onClick={() => apply((p) => centerOnPage(p, onGrid, "v"))} />
        <Btn icon={MoveDiagonal2} label="Same width and height as the first selected" onClick={() => apply((p) => matchSize(p, onGrid, "both"))} disabled={n < 2} />
      </div>
      <div className="st-arr__group" aria-label="Stacking order">
        <Btn icon={BringToFront} label="Bring to front (Ctrl+Shift+])" onClick={() => onRestack("front")} disabled={selectedIds.length === 0} />
        <Btn icon={Forward} label="Bring forward (Ctrl+])" onClick={() => onRestack("forward")} disabled={selectedIds.length === 0} />
        <Btn icon={Backward} label="Send backward (Ctrl+[)" onClick={() => onRestack("backward")} disabled={selectedIds.length === 0} />
        <Btn icon={SendToBack} label="Send to back (Ctrl+Shift+[)" onClick={() => onRestack("back")} disabled={selectedIds.length === 0} />
      </div>
      <div className="st-arr__group" aria-label="Format painter">
        <Btn icon={Paintbrush} label="Copy format of the selected visual (Ctrl+Shift+C)" onClick={onCopyFormat} disabled={selectedIds.length === 0} />
        <Btn icon={ClipboardPaste} label="Paste format onto the selected visuals (Ctrl+Shift+V)" onClick={onPasteFormat} disabled={selectedIds.length === 0 || !canPasteFormat} />
      </div>
      <div className="st-arr__menuwrap" ref={menuRef}>
        <button type="button" className="st-arr__tidy" onClick={() => setMenu((v) => !v)} aria-haspopup="menu" aria-expanded={menu}>
          <Sparkles size={15} /> Auto-arrange <ChevronDown size={13} />
        </button>
        {menu ? (
          <div className="st-arr__menu" role="menu">
            <button role="menuitem" onClick={() => run((p) => straighten(p), "Straightened — nothing was moved to a different row or column")}>
              <strong>Straighten (recommended)</strong>
              <small>Keeps your layout exactly as designed. Lines up edges that are almost aligned and closes small gaps between neighbours.</small>
            </button>
            <button role="menuitem" onClick={() => run((p) => compactUp(p, onGrid.length > 1 ? onGrid : undefined), onGrid.length > 1 ? "Closed the gaps above the selected visuals" : "Closed the empty space above each visual")}>
              <strong>{onGrid.length > 1 ? "Close empty space above the selection" : "Close empty space above every visual"}</strong>
              <small>Slides visuals up into empty space. Nothing is resized.</small>
            </button>
            <div className="st-arr__menu-sep">Rebuild the whole layout (changes the structure)</div>
            <button role="menuitem" onClick={() => run((p) => tidyRows(p, "fill"), "Rebuilt as full-width rows")}>
              <strong>Neat rows, full width</strong>
              <small>Re-flows every visual into straight rows that reach both screen edges. Reading order decides the rows, so side-by-side columns can change.</small>
            </button>
            <button role="menuitem" onClick={() => run((p) => tidyRows(p, "keep"), "Rebuilt as centered rows")}>
              <strong>Neat rows, keep my sizes</strong>
              <small>Same, but nothing is resized. Each row is centered.</small>
            </button>
          </div>
        ) : null}
      </div>
      <div className="st-arr__group st-arr__grid" title="Grid size: fine gives twice as many columns and rows to place and size visuals precisely">
        <Grid3x3 size={15} />
        <span className="st-arr__gridlabel">Grid</span>
        {(["standard", "fine"] as Density[]).map((d) => (
          <button key={d} type="button" className={`st-arr__seg${density === d ? " is-on" : ""}`} aria-pressed={density === d} onClick={() => setDensity(d)}>
            {d === "standard" ? `Standard (${DENSITY_COLUMNS.standard})` : `Fine (${DENSITY_COLUMNS.fine})`}
          </button>
        ))}
      </div>
      <Btn icon={Keyboard} label="Keyboard shortcuts (?)" onClick={onShortcuts} />
    </div>
  );
}
